import { hostnameMatchesDomain } from 'src/functions/url.functions';
import type { tWorkspaceAttribution } from './Workspace.attribution';

// GRO-1265: which AI assistant sent a visit. Same list as the site collector
// (leadsales-attribution.js v1.5) and hubspot-bridge (`signup_ai_assistant`).
// ChatGPT and Copilot tag their links with utm_source; Gemini, Claude,
// Perplexity, Meta AI and the rest only send a referrer (or nothing, from
// their apps). Hosts match exactly or as a parent domain (l.meta.ai).
export type tAiAssistant =
  | 'ChatGPT'
  | 'Gemini'
  | 'Claude'
  | 'Perplexity'
  | 'Copilot'
  | 'Meta AI'
  | 'DeepSeek'
  | 'Grok'
  | 'Mistral'
  | 'Other AI';

const AI_HOSTS: ReadonlyArray<readonly [string, tAiAssistant]> = [
  ['chatgpt.com', 'ChatGPT'],
  ['chat.openai.com', 'ChatGPT'],
  ['gemini.google.com', 'Gemini'],
  ['bard.google.com', 'Gemini'],
  ['aistudio.google.com', 'Gemini'],
  ['notebooklm.google.com', 'Gemini'],
  ['claude.ai', 'Claude'],
  ['perplexity.ai', 'Perplexity'],
  ['copilot.microsoft.com', 'Copilot'],
  ['copilot.cloud.microsoft', 'Copilot'],
  ['copilot.com', 'Copilot'],
  ['edgeservices.bing.com', 'Copilot'],
  ['meta.ai', 'Meta AI'],
  ['deepseek.com', 'DeepSeek'],
  ['grok.com', 'Grok'],
  ['mistral.ai', 'Mistral'],
  ['duck.ai', 'Other AI'],
  ['you.com', 'Other AI'],
  ['poe.com', 'Other AI'],
];

// utm_source values that name an assistant without being one of its hosts.
// Exact names only, never substrings: `facebook-chatgpt` is a Facebook campaign.
const SOURCE_NAMES: ReadonlyArray<readonly [string, tAiAssistant]> = [
  ['chatgpt', 'ChatGPT'],
  ['openai', 'ChatGPT'],
  ['gemini', 'Gemini'],
  ['claude', 'Claude'],
  ['perplexity', 'Perplexity'],
  ['copilot', 'Copilot'],
  ['deepseek', 'DeepSeek'],
  ['grok', 'Grok'],
  ['mistral', 'Mistral'],
];

export function aiAssistantFromHost(
  hostname?: string,
): tAiAssistant | undefined {
  const host = hostname?.trim().toLowerCase();
  if (!host) return undefined;
  return AI_HOSTS.find(([listed]) =>
    hostnameMatchesDomain(host, [listed]),
  )?.[1];
}

function aiAssistantFromSource(source: string): tAiAssistant | undefined {
  const value = source.trim().toLowerCase();
  return (
    aiAssistantFromHost(value) ??
    SOURCE_NAMES.find(([name]) => value === name)?.[1]
  );
}

// A touch is AI when its utm_source names an assistant or, with no utm_source,
// when its referrer does. A touch tagged by another campaign is never AI.
function touchAssistant(
  source: string | undefined,
  ref: string | undefined,
): tAiAssistant | undefined {
  if (source?.trim()) return aiAssistantFromSource(source);
  return aiAssistantFromHost(ref);
}

/** The assistant behind a signup: the last touch's, else the first touch's. */
export function aiAssistantOf(
  attribution?: Pick<
    tWorkspaceAttribution,
    'utm_source' | 'ref' | 'ft_source' | 'ft_ref'
  >,
): tAiAssistant | undefined {
  if (!attribution) return undefined;
  return (
    touchAssistant(attribution.utm_source, attribution.ref) ??
    touchAssistant(attribution.ft_source, attribution.ft_ref)
  );
}
